import { useState, useEffect, useCallback } from 'react';
import debtorApi from '../../../../api/debtorApi';
import { convertCurrencyToNumber } from '../utils/formatCurrency';

export interface DebtorFormData {
  cif: string;
  debtor_type: string;
  is_entrepreneur: boolean;
  debtor_profile: string;
  full_name: string;
  id_type: string;
  id_number: string;
  birth_date: string;
  birth_place: string;
  gender: string;
  marital_status: string;
  education: string;
  photo_url: string;
  mother_maiden_name: string;
  address: string;
  village: string;
  district: string;
  city: string;
  province: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
  phone: string;
  email: string;
  occupation: string;
  company_name: string;
  monthly_income: string;
  source_of_income: string;
  spouse_name: string;
  spouse_id_number: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  bank_relationship: string;
  last_facility: string;
  last_credit_quality: string;
  last_arrears: string;
  relationship_start_date: string;
  slik_document_url: string;
  slik_effective_plafon: string;
  slik_outstanding: string;
  slik_worst_quality: string;
  slik_data_month: string;
  slik_position_date: string;
  slik_score: string;
  slik_status: string;
  slik_last_check: string;
  relations: Array<{
    type: string;
    name: string;
    birth_info: string;
    relation: string;
    photo_url: string;
  }>;
  business_documents: Array<{
    file_url: string;
    title: string;
    notes: string;
  }>;
  document_status: string;
  notes: string;
  status: string;
}

const INITIAL_FORM_DATA: DebtorFormData = {
  cif: '',
  debtor_type: 'individual',
  is_entrepreneur: false,
  debtor_profile: '',
  full_name: '',
  id_type: 'ktp',
  id_number: '',
  birth_date: '',
  birth_place: '',
  gender: '',
  marital_status: '',
  education: '',
  photo_url: '',
  mother_maiden_name: '',
  address: '',
  village: '',
  district: '',
  city: '',
  province: '',
  postal_code: '',
  latitude: null,
  longitude: null,
  phone: '',
  email: '',
  occupation: '',
  company_name: '',
  monthly_income: '',
  source_of_income: '',
  spouse_name: '',
  spouse_id_number: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  bank_relationship: '',
  last_facility: '',
  last_credit_quality: '',
  last_arrears: '',
  relationship_start_date: '',
  slik_document_url: '',
  slik_effective_plafon: '',
  slik_outstanding: '',
  slik_worst_quality: '',
  slik_data_month: '',
  slik_position_date: '',
  slik_score: '',
  slik_status: '',
  slik_last_check: '',
  relations: [],
  business_documents: [],
  document_status: 'incomplete',
  notes: '',
  status: 'active',
};

export const useDebtorForm = (debtorId: string | null) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<DebtorFormData>(INITIAL_FORM_DATA);

  // Generate CIF
  const generateCIF = useCallback(() => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0');
    setFormData(prev => ({ ...prev, cif: `CIF${timestamp}${random}` }));
  }, []);

  // Load debtor data
  const loadDebtor = useCallback(async () => {
    if (!debtorId) return;

    setLoading(true);
    try {
      const debtor = await debtorApi.getById(parseInt(debtorId));
      if (debtor) {
        const debtorData = debtor as any; // Type assertion for extended fields that may not be in DB yet
        setFormData({
          ...INITIAL_FORM_DATA,
          ...debtorData,
          monthly_income: debtor.monthly_income?.toString() || '',
          last_arrears: debtorData.last_arrears?.toString() || '',
          slik_effective_plafon: debtorData.slik_effective_plafon?.toString() || '',
          slik_outstanding: debtorData.slik_outstanding?.toString() || '',
          slik_score: debtor.slik_score?.toString() || '',
          relations: debtorData.relations || [],
          business_documents: debtorData.business_documents || [],
        });
      }
    } catch (error) {
      console.error('Error loading debtor:', error);
    } finally {
      setLoading(false);
    }
  }, [debtorId]);

  // Initialize form
  useEffect(() => {
    if (debtorId) {
      loadDebtor();
    } else {
      generateCIF();
    }
  }, [debtorId, loadDebtor, generateCIF]);

  // Handle field change
  const handleChange = useCallback((field: string, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Reset id_number when id_type changes
      if (field === 'id_type') {
        newData.id_number = '';
      }

      return newData;
    });
  }, []);

  // Handle file upload
  // Note: For now, we'll store file as base64 or use a file storage service
  // You may need to implement a file upload endpoint in the backend
  const handleFileUpload = useCallback(
    async (file: File, field: string) => {
      try {
        // Option 1: Convert to base64 (for small files)
        const reader = new FileReader();
        const fileUrl = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Option 2: If you have an upload endpoint, use this instead:
        // const formData = new FormData();
        // formData.append('file', file);
        // const response = await apiClient.upload<{ file_url: string }>('/upload', formData);
        // const fileUrl = response.file_url || response.url || (response as any).fileUrl;

        handleChange(field, fileUrl);
        return fileUrl;
      } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
      }
    },
    [handleChange]
  );

  // Add relation
  const addRelation = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      relations: [
        ...prev.relations,
        {
          type: 'individual',
          name: '',
          birth_info: '',
          relation: '',
          photo_url: '',
        },
      ],
    }));
  }, []);

  // Update relation
  const updateRelation = useCallback((index: number, field: string, value: any) => {
    setFormData(prev => {
      const relations = [...prev.relations];
      relations[index] = { ...relations[index], [field]: value };
      return { ...prev, relations };
    });
  }, []);

  // Remove relation
  const removeRelation = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      relations: prev.relations.filter((_, i) => i !== index),
    }));
  }, []);

  // Add business document
  const addBusinessDocument = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      business_documents: [
        ...prev.business_documents,
        {
          file_url: '',
          title: '',
          notes: '',
        },
      ],
    }));
  }, []);

  // Update business document
  const updateBusinessDocument = useCallback((index: number, field: string, value: any) => {
    setFormData(prev => {
      const docs = [...prev.business_documents];
      docs[index] = { ...docs[index], [field]: value };
      return { ...prev, business_documents: docs };
    });
  }, []);

  // Remove business document
  const removeBusinessDocument = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      business_documents: prev.business_documents.filter((_, i) => i !== index),
    }));
  }, []);

  // Prepare data for save
  const prepareDataForSave = useCallback((): any => {
    // Convert currency strings to numbers
    const monthlyIncome = convertCurrencyToNumber(formData.monthly_income);
    const lastArrears = convertCurrencyToNumber(formData.last_arrears);
    const slikEffectivePlafon = convertCurrencyToNumber(formData.slik_effective_plafon);
    const slikOutstanding = convertCurrencyToNumber(formData.slik_outstanding);
    const slikScore = formData.slik_score ? parseFloat(formData.slik_score) : null;

    // Helper function to convert empty strings to null
    const cleanValue = (value: any): any => {
      if (value === '' || value === undefined) return null;
      return value;
    };

    // Build complete data object with all fields
    const dataToSave: any = {
      // Identity fields
      cif: formData.cif || null,
      debtor_type: formData.debtor_type || 'individual',
      is_entrepreneur: formData.is_entrepreneur || false,
      debtor_profile: cleanValue(formData.debtor_profile),
      full_name: formData.full_name,
      id_type: formData.id_type || 'ktp',
      id_number: formData.id_number,
      birth_date: cleanValue(formData.birth_date),
      birth_place: cleanValue(formData.birth_place),
      gender: cleanValue(formData.gender),
      marital_status: cleanValue(formData.marital_status),
      education: cleanValue(formData.education),
      photo_url: cleanValue(formData.photo_url),
      mother_maiden_name: cleanValue(formData.mother_maiden_name),

      // Address fields
      address: cleanValue(formData.address),
      village: cleanValue(formData.village),
      district: cleanValue(formData.district),
      city: cleanValue(formData.city),
      province: cleanValue(formData.province),
      postal_code: cleanValue(formData.postal_code),
      latitude: formData.latitude,
      longitude: formData.longitude,

      // Contact fields
      phone: cleanValue(formData.phone),
      email: cleanValue(formData.email),

      // Financial fields
      occupation: cleanValue(formData.occupation),
      company_name: cleanValue(formData.company_name),
      monthly_income: monthlyIncome,
      source_of_income: cleanValue(formData.source_of_income),
      bank_relationship: cleanValue(formData.bank_relationship),
      last_facility: cleanValue(formData.last_facility),
      last_credit_quality: cleanValue(formData.last_credit_quality),
      last_arrears: lastArrears,
      relationship_start_date: cleanValue(formData.relationship_start_date),

      // Family fields
      spouse_name: cleanValue(formData.spouse_name),
      spouse_id_number: cleanValue(formData.spouse_id_number),
      emergency_contact_name: cleanValue(formData.emergency_contact_name),
      emergency_contact_phone: cleanValue(formData.emergency_contact_phone),

      // SLIK fields
      slik_document_url: cleanValue(formData.slik_document_url),
      slik_effective_plafon: slikEffectivePlafon,
      slik_outstanding: slikOutstanding,
      slik_worst_quality: cleanValue(formData.slik_worst_quality),
      slik_data_month: cleanValue(formData.slik_data_month),
      slik_position_date: cleanValue(formData.slik_position_date),
      slik_score: slikScore,
      slik_status: cleanValue(formData.slik_status),
      slik_last_check: cleanValue(formData.slik_last_check),

      // Relations and documents (as JSON)
      relations:
        formData.relations && formData.relations.length > 0
          ? JSON.stringify(formData.relations)
          : null,
      business_documents:
        formData.business_documents && formData.business_documents.length > 0
          ? JSON.stringify(formData.business_documents)
          : null,

      // Status fields
      document_status: formData.document_status || 'incomplete',
      notes: cleanValue(formData.notes),
      status: formData.status || 'active',
    };

    return dataToSave;
  }, [formData]);

  // Save debtor
  const saveDebtor = useCallback(async () => {
    setSaving(true);
    try {
      const dataToSave = prepareDataForSave();

      if (debtorId) {
        await debtorApi.update(parseInt(debtorId), dataToSave);
      } else {
        await debtorApi.create(dataToSave);
      }

      return true;
    } catch (error) {
      console.error('Error saving debtor:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [debtorId, prepareDataForSave]);

  return {
    loading,
    saving,
    formData,
    handleChange,
    handleFileUpload,
    addRelation,
    updateRelation,
    removeRelation,
    addBusinessDocument,
    updateBusinessDocument,
    removeBusinessDocument,
    saveDebtor,
  };
};
